import { useFormik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { requireIdToken } from "../../utils/firebaseAuth";
import { useModal } from "../../context/ModalContext";
import { useRouter } from "next/router";
import { toast } from "react-hot-toast";
import { useRef, useState } from "react";
import { Input, TextArea } from "@jammable/ui-components/Forms";
import { Spinner } from "@jammable/ui-components/Loader";
import UploadImage from "../UploadImage";
import NewerVersionSelect from "../NewerVersionSelect";
import CategoryPicker from "../CategoryPicker";

// Design language F form pieces, shared with UploadVoiceModal's look:
// lavender fields, no borders, pills.
const fieldLabel = "tw-text-sm tw-font-extrabold tw-text-jam-ink";
const errorText = "tw-text-xs tw-font-bold tw-text-[#8A1C3B]";

const EditModelModal = ({ model, after, reroute }) => {
  const validationSchema = yup.object().shape({
    modelName: yup
      .string()
      .required("The model name is required")
      .max(40, "40 characters maximum."),
    modelDescription: yup.string().max(200, "200 characters maximum."),
  });
  const { closeModal } = useModal();
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localImage, setLocalImage] = useState(null);

  const [isPublic, setIsPublic] = useState(!model?.private && model?.isPublic);
  const [newerVersion, setNewerVersion] = useState(model?.newerVersion || null);

  const formik = useFormik({
    initialValues: {
      modelName: model?.artistname,
      modelDescription: model?.description,
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      const token = await requireIdToken();

      let imageUrl;

      if (localImage) {
        const file = await imageUploadRef.current.getCroppedImageFile();
        const response = await axios.get("/api/s3/getPresignedUrl", {
          params: { type: "voice" },
          headers: { Authorization: token },
        });
        const { url, absoluteUrl } = response.data;

        const options = { headers: { "Content-Type": file.type } };

        await axios.put(url, file, options);
        imageUrl = absoluteUrl;
      }

      try {
        axios
          .put(
            "/api/user/updateUserModel",
            {
              ...values,
              modelid: model._id,
              isPublic: isPublic,
              image: imageUrl,
              categories: newCategories,
              newerVersionId: newerVersion?._id || null,
            },
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: token,
              },
            },
          )
          .then(() => {
            if (after) {
              after();
            }
            closeModal("edit-model-modal");
            if (reroute) {
              router.push(`/${model?.urlParam}`);
            }
            toast.success("Voice updated!");
          })
          .catch((err) => {
            setError(err.response.data.message);
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (error) {
        console.error(error);
      }
    },
  });

  const [newCategories, setNewCategories] = useState(() => {
    const initialCategories =
      model?.badges?.map((item) => item.trim().toLowerCase()) || [];
    return initialCategories;
  });

  const imageUploadRef = useRef(null);

  return (
    <div className="tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-gap-4 tw-text-jam-ink">
      <h2 className="jam-display tw-m-0 tw-pr-10 tw-text-[26px] tw-font-extrabold">
        Edit voice
      </h2>
      <form
        onChange={() => {
          setError(null);
        }}
        className="tw-flex tw-flex-col tw-gap-4"
        onSubmit={formik.handleSubmit}
      >
        {error && (
          <div className="tw-rounded-[18px] tw-bg-[#FDE4EC] tw-px-4 tw-py-3 tw-text-sm tw-font-bold tw-text-[#8A1C3B]">
            {error}
          </div>
        )}
        <div className="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-4 sm:tw-flex-row sm:tw-items-start">
          <div className="tw-w-max tw-shrink-0">
            <UploadImage
              image={model?.image?.url}
              setImage={setLocalImage}
              ref={imageUploadRef}
            />
          </div>
          <div className="tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-gap-3">
            <label className="tw-flex tw-flex-col tw-gap-1.5">
              <span className={fieldLabel}>Voice name</span>
              <Input
                placeholder="Voice name"
                name="modelName"
                value={formik.values.modelName}
                onChange={formik.handleChange}
                className="tw-h-[52px] tw-rounded-full tw-border-0 tw-bg-jam-lav tw-px-5 tw-text-[15px] tw-text-jam-ink"
                onBlur={formik.handleBlur}
              />
            </label>
            {formik.touched.modelName && formik.errors.modelName && (
              <div className={errorText}>{formik.errors.modelName}</div>
            )}
            <label className="tw-flex tw-flex-col tw-gap-1.5">
              <span className={fieldLabel}>Description</span>
              <TextArea
                value={formik.values.modelDescription}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name="modelDescription"
                className="tw-h-auto tw-min-h-24 tw-w-full tw-rounded-[22px] tw-border-0 tw-bg-jam-lav tw-px-5 tw-text-jam-ink"
                aria-label="Describe your model!"
                placeholder="(Optional) Describe your voice..."
              />
            </label>
            {formik.touched.modelDescription &&
              formik.errors.modelDescription && (
                <div className={errorText}>
                  {formik.errors.modelDescription}
                </div>
              )}
          </div>
        </div>

        <NewerVersionSelect
          setNewerVersion={setNewerVersion}
          newerVersion={newerVersion}
        />
        <CategoryPicker
          newCategories={newCategories}
          setNewCategories={setNewCategories}
        />

        <div className="tw-flex tw-flex-col tw-gap-2">
          <div className={fieldLabel}>Who can use it</div>
          <div className="tw-flex tw-gap-1 tw-self-stretch tw-rounded-full tw-bg-jam-lav tw-p-1 tw-text-[13px] tw-font-extrabold sm:tw-self-start">
            {[
              [false, "Private, only me"],
              [true, "Public in the library"],
            ].map(([value, label]) => (
              <button
                key={label}
                type="button"
                aria-pressed={!!isPublic === value}
                onClick={() => setIsPublic(value)}
                className={`tw-flex-grow tw-cursor-pointer tw-rounded-full tw-border-0 tw-px-4 tw-py-2 ${
                  !!isPublic === value
                    ? "tw-bg-white tw-text-jam-purple"
                    : "tw-bg-transparent tw-text-jam-ink"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <button
          disabled={loading}
          type="submit"
          className="tw-mt-1 tw-flex tw-h-[54px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-lime tw-text-base tw-font-extrabold tw-text-jam-ink disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
        >
          {loading ? (
            <Spinner size={4} className="tw-border-3 tw-border-jam-ink" />
          ) : (
            "Save"
          )}
        </button>
      </form>
    </div>
  );
};

export default EditModelModal;
